import React from 'react';
import type { BoxProps } from '@mui/material';
import { Box, LinearProgress } from '@mui/material';
import { useCommonTranslation } from 'c-translation';

type Props = Omit<BoxProps, 'children'> & { loading?: boolean; children: React.ReactNode };

/**
 * Wraps the table while a search is in flight: the rows already on screen stay readable, dimmed
 * and inert, under a progress bar along the top. (The first load has no rows yet, so
 * PaginatedEntityList draws skeleton rows there instead.) Layout props go on the outer box.
 */
const TableLoadingRegion = ({ loading = false, children, ...boxProps }: Props) => {
    const loadingLabel = useCommonTranslation('Component.LoadingSpinner.label');
    return (
        <Box
            position="relative"
            data-testid="table-loading-region"
            aria-busy={loading}
            {...boxProps}
        >
            {loading && (
                <LinearProgress
                    aria-label={loadingLabel}
                    // Above a sticky table header.
                    sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 3 }}
                />
            )}
            <Box
                data-testid="table-loading-content"
                display="flex"
                flex={1}
                minWidth={0}
                minHeight={0}
                sx={{
                    opacity: loading ? 0.5 : 1,
                    pointerEvents: loading ? 'none' : undefined,
                    transition: 'opacity 150ms',
                }}
            >
                {children}
            </Box>
        </Box>
    );
};

export default TableLoadingRegion;
